/*
  Fond video du hero d'accueil (sections/vd-yc-hero-list.liquid).

  Ce fichier n'est charge que si une video de fond est choisie dans l'editeur.

  Principes :
  - la lecture est declenchee par un IntersectionObserver : rien ne part tant
    que le hero n'est pas a l'ecran, et la lecture est mise en pause des qu'il
    en sort (CPU, batterie) ;
  - l'image de fond reste la premiere image affichee ; la video n'apparait
    (fondu) qu'une fois la lecture reellement demarree, donc aucun flash et
    aucun trou si la lecture est refusee ;
  - `prefers-reduced-motion: reduce` : on ne lance rien, l'image reste. La
    preference est relue a chaud, donc un visiteur qui la change en cours de
    route est respecte immediatement ;
  - mobile : la video est lue si le reglage « Lire aussi la video sur mobile »
    est coche (c'est le defaut) ;
  - iOS en mode economie d'energie refuse la lecture automatique : dans ce cas
    on retente une seule fois au premier geste du visiteur, sans rien afficher ;
  - YouTube / Vimeo : l'iframe est injectee seulement au moment ou le hero
    devient visible, jamais dans le HTML initial.
*/
(function () {
  'use strict';

  var SELECTOR = '[data-vd-hero-video]';
  var MOBILE_QUERY = '(max-width: 749px)';
  var REDUCED_QUERY = '(prefers-reduced-motion: reduce)';

  function media(query) {
    return window.matchMedia ? window.matchMedia(query) : null;
  }

  function prefersReducedMotion() {
    var m = media(REDUCED_QUERY);
    return !!(m && m.matches);
  }

  function isMobile() {
    var m = media(MOBILE_QUERY);
    return !!(m && m.matches);
  }

  function onMediaChange(query, fn) {
    var m = media(query);
    if (!m) return;
    if (m.addEventListener) m.addEventListener('change', fn);
    else if (m.addListener) m.addListener(fn);
  }

  function init(root) {
    if (!root || root.dataset.vdHeroVideoReady === '1') return;
    root.dataset.vdHeroVideoReady = '1';

    var provider = root.dataset.provider || 'none';
    if (provider === 'none') return;

    var isFile = provider === 'shopify' || provider === 'asset';
    var video = root.querySelector('[data-vd-hero-video-player]');
    var frame = root.querySelector('[data-vd-hero-video-frame]');
    var autoplay = root.dataset.autoplay === 'true';
    var allowMobile = root.dataset.mobile === 'true';
    var started = false;
    var visible = false;
    var gestureArmed = false;

    function allowed() {
      if (!autoplay) return false;
      if (prefersReducedMotion()) return false;
      if (!allowMobile && isMobile()) return false;
      return true;
    }

    function reveal() {
      root.classList.add('is-video-ready');
    }

    function hide() {
      root.classList.remove('is-video-ready');
    }

    /* ----------------------------------------------------- lecture fichier */

    function playFile() {
      if (!video) return;
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          /* Lecture refusee (economie d'energie iOS, economiseur de donnees,
             onglet en arriere-plan). On reste sur l'image de fond et on
             retente une seule fois au premier geste du visiteur. */
          started = false;
          hide();
          armGesture();
        });
      }
    }

    function armGesture() {
      if (gestureArmed) return;
      gestureArmed = true;
      var retry = function () {
        document.removeEventListener('touchstart', retry);
        document.removeEventListener('click', retry);
        document.removeEventListener('keydown', retry);
        if (!started && visible && allowed()) startFile();
      };
      document.addEventListener('touchstart', retry, { once: true, passive: true });
      document.addEventListener('click', retry, { once: true });
      document.addEventListener('keydown', retry, { once: true });
    }

    function startFile() {
      if (!video || started) return;
      started = true;

      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      if (video.preload === 'none') video.preload = 'auto';

      video.addEventListener('playing', reveal);
      playFile();
    }

    /* -------------------------------------------------------- YouTube / Vimeo */

    function embedSrc() {
      var base = root.dataset.embedBase || '';
      var id = root.dataset.videoId || '';
      var p = [];

      function add(k, v) {
        p.push(encodeURIComponent(k) + '=' + encodeURIComponent(v));
      }

      if (provider === 'youtube') {
        add('autoplay', '1');
        add('mute', '1');
        add('loop', '1');
        if (id) add('playlist', id);
        add('controls', '0');
        add('playsinline', '1');
        add('rel', '0');
        add('modestbranding', '1');
        add('disablekb', '1');
      } else {
        /* vimeo */
        add('autoplay', '1');
        add('muted', '1');
        add('loop', '1');
        add('playsinline', '1');
        add('background', '1');
        add('dnt', '1');
      }
      return base + '?' + p.join('&');
    }

    function startFrame() {
      if (!frame || started) return;
      started = true;

      var iframe = document.createElement('iframe');
      iframe.className = 'vd-herolist__video-el';
      iframe.setAttribute('tabindex', '-1');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.setAttribute('loading', 'lazy');
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
      iframe.addEventListener('load', reveal, { once: true });
      iframe.src = embedSrc();

      frame.replaceChildren
        ? frame.replaceChildren(iframe)
        : ((frame.innerHTML = ''), frame.appendChild(iframe));
    }

    function start() {
      if (!allowed()) return;
      if (isFile) startFile();
      else startFrame();
    }

    /* --------------------------------------------------------- declenchement */

    function enter() {
      visible = true;
      if (!started) {
        start();
      } else if (video && video.paused) {
        playFile();
      }
    }

    function leave() {
      visible = false;
      if (video && started && !video.paused) video.pause();
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) enter();
            else leave();
          });
        },
        /* rootMargin genereux : sur un hero deja a l'ecran au chargement, la
           lecture part immediatement ; ailleurs elle part juste avant l'entree. */
        { threshold: 0, rootMargin: '200px 0px' }
      );
      observer.observe(root);
    } else {
      /* Repli sans IntersectionObserver : on lance apres le chargement. */
      if (document.readyState === 'complete') enter();
      else window.addEventListener('load', enter, { once: true });
    }

    /* La preference d'animation peut changer en cours de visite. */
    onMediaChange(REDUCED_QUERY, function (e) {
      if (e.matches) {
        if (video) video.pause();
        started = false;
        hide();
      } else if (visible) {
        start();
      }
    });

    /* Rotation / redimensionnement : on tente une fois si on repasse en desktop. */
    if (!allowMobile) {
      onMediaChange(MOBILE_QUERY, function () {
        if (!started && visible) start();
      });
    }

    /* Onglet en arriere-plan : le navigateur coupe de lui-meme une video sans
       son pour economiser la batterie, et refuse meme de la demarrer (Chrome :
       « video-only background media was paused to save power »). On reprend
       donc la main au retour au premier plan, y compris si le premier essai
       avait ete refuse : c'est le cas courant d'une page ouverte dans un
       nouvel onglet. */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        if (video && started) video.pause();
      } else if (visible) {
        if (!started) start();
        else if (video && video.paused) playFile();
      }
    });
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  /* Editeur de theme : la section est re-injectee a chaque modification. */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
